// PD-2 — front/back matter reading view. Matter (foreword / afterword /
// about-the-author) has no original counterpart, so this is the reading view
// with everything pairing-dependent removed: one column, no original pane, no
// sync, no gloss popover, no bookmarks/?loc= (matter blocks carry no stable
// ids). It keeps the book typography, the retelling font-size pref, the copy
// handler, prev/next unit navigation, and the copyright footer.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { ArrowLeft } from "lucide-react";
import { AppNav } from "@/components/app-nav";
import { Button } from "@bindery/ui/components/ui/button";
import { copyrightNotice } from "@/lib/copyright";
import { flushPerformanceChunk, getDeviceContext, useLogger } from "@/logging";
import { getPref, setPref } from "@/lib/prefs";
import {
  DEFAULT_FONT,
  type FontSizePrefs,
} from "@bindery/shared/corpus/reader";
import { DocumentPane } from "./document-pane";
import { FontSizeControl } from "./font-size-control";
import { PageTurnNav, UnitNavButtons } from "./unit-nav";
import { VolumeEnd } from "./volume-end";
import { adjacentUnits, type ReadingUnit } from "./reading-order";
import {
  useCatalog,
  useMatterData,
  usePremiumMatterData,
  type MatterData,
} from "./use-corpus";
import { UpsellPlate } from "./premium-gate";
import { lockedMatter, useEntitlementGate } from "./locked-unit";
import { BOOK_CONFIGS } from "@bindery/convex/model/bookConfig";
import { anchorChapterOfMatter } from "@bindery/convex/model/volume";
import { findBook, findMatter } from "@/lib/corpus";
import type {
  CatalogBook,
  CatalogMatter,
} from "@bindery/shared/corpus/catalog";
import { useCopyHandler } from "./use-copy-handler";
import { useMediaQuery } from "./use-media-query";
import { ReadingNotFound, ReadingSkeleton } from "./reading-frame";
import { useReadingTelemetry } from "./use-reading-telemetry";

function MatterReader({ data }: { data: MatterData }) {
  const { book, matter, model } = data;
  const log = useLogger();
  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });

  const paneRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState<FontSizePrefs>(() =>
    getPref("fontSize", DEFAULT_FONT),
  );
  const isNarrow = useMediaQuery("(max-width: 767px)");

  // Block ids in document order — see the same derivation in `reading-view`.
  const matterBlockIds = useMemo(
    () =>
      model.blocks
        .map((block) => block.id)
        .filter((id): id is string => id !== undefined),
    [model],
  );

  // Device context + long-task chunk boundary, same rationale as the reading
  // view's chapter open.
  useEffect(() => {
    logRef.current.event("reading.open", {
      book: book.slug,
      matter: matter.slug,
      sourceLanguage: book.sourceLanguage,
      ...getDeviceContext(),
    });
    flushPerformanceChunk("unit_open", {
      book: book.slug,
      matter: matter.slug,
    });
  }, [book.slug, book.sourceLanguage, matter.slug]);

  // Matter is read the same way a chapter is, so it is measured the same way.
  // One column, so the rebind key is constant — there is no paned/interleaved
  // swap here to replace the node under the observer.
  useReadingTelemetry({
    containerRef: paneRef,
    bookSlug: book.slug,
    unitKey: matter.slug,
    blockIds: matterBlockIds,
    columnKey: "matter",
  });

  useCopyHandler({
    retellingRef: paneRef,
    bookTitle: book.title,
    onCopy: (info) => {
      logRef.current.event("reading.copy", {
        book: book.slug,
        matter: matter.slug,
        length: info.length,
        truncated: info.truncated,
      });
    },
  });

  const unit: ReadingUnit = {
    kind: "matter",
    slug: matter.slug,
    title: matter.title,
    premium: matter.premium === true,
    premiumProductId: matter.premiumProductId,
  };
  const { prev, next } = adjacentUnits(book, unit);

  const setFont = (value: number): void => {
    // Matter uses (and writes back) the retelling size; a linked pref moves
    // both panes together exactly as the chapter view's control does.
    const updated: FontSizePrefs = fontSize.linked
      ? { retelling: value, original: value, linked: true }
      : { ...fontSize, retelling: value };
    setFontSize(updated);
    setPref("fontSize", updated);
  };

  return (
    <div className="bg-background flex h-svh flex-col">
      {/* Toolbar — same bones as the chapter reader's, minus the pairing
          controls, and like it the site controls ride at the end of this row
          rather than floating over its corner. */}
      <header className="border-border flex flex-col border-b">
        <div className="flex items-center gap-2 px-3 py-2">
          <Button
            asChild
            variant="ghost"
            size="icon-sm"
            aria-label="Back to library"
          >
            <Link to="/">
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
          <div className="min-w-0 flex-1">
            <p className="font-display text-foreground truncate text-sm font-bold">
              {book.title}
            </p>
            <p className="text-muted-foreground truncate font-serif text-xs">
              {matter.title}
            </p>
          </div>
          {/* Below `md` the text size AND the page turns move to a second row
              so the title keeps its width — the same treatment as the chapter
              reader's. Text size moves rather than disappearing: it is a
              low-vision affordance, not a two-pane convenience. */}
          <AppNav variant="inline">
            {!isNarrow && (
              <>
                <FontSizeControl
                  label="Text size"
                  value={fontSize.retelling}
                  onChange={setFont}
                />
                <UnitNavButtons bookSlug={book.slug} prev={prev} next={next} />
              </>
            )}
          </AppNav>
        </div>

        {isNarrow && (
          <div className="border-border/70 bg-background/80 flex items-center justify-center gap-3 border-t px-4 py-2 backdrop-blur">
            <FontSizeControl
              label="Text size"
              value={fontSize.retelling}
              onChange={setFont}
            />
            <UnitNavButtons bookSlug={book.slug} prev={prev} next={next} />
          </div>
        )}
      </header>

      {/* `<main>` landmark around the reading content — matter's article was
          the page's sole content but had no containing landmark (axe
          landmark-one-main/region). */}
      <main className="min-h-0 flex-1">
        <DocumentPane
          ref={paneRef}
          model={model}
          pane="retelling"
          fontSize={fontSize.retelling}
          footer={
            <PageTurnNav
              bookSlug={book.slug}
              prev={prev}
              next={next}
              volumeEnd={<VolumeEnd book={book} unit={unit} />}
            />
          }
        />
      </main>

      <footer className="border-border border-t px-4 py-1.5">
        <p className="text-muted-foreground truncate font-serif text-[11px]">
          {copyrightNotice(book.title)}
        </p>
      </footer>
    </div>
  );
}

/**
 * Matter dispatch, mirroring `ChapterLoader`: a free document loads from the
 * static corpus; a paywalled one goes through the entitlement gate. The catalog
 * carries the flag, so the decision needs no server round-trip.
 */
export function MatterLoader({
  bookSlug,
  matterSlug,
}: {
  bookSlug: string;
  matterSlug: string;
}) {
  const catalog = useCatalog();
  const book = findBook(catalog, bookSlug);
  const matter = book ? findMatter(book, matterSlug) : undefined;
  if (!book || !matter) {
    return <ReadingNotFound label="That page could not be found." />;
  }
  if (matter.premium) {
    // Keyed on the subject for the same reason the chapter gate is: page-turning
    // between two locked documents reconciles this component in place, and a
    // held `entitled: true` from the previous one would render the gated loader
    // for a document the reader may not own.
    return (
      <PremiumMatterGate
        key={`${bookSlug}:${matterSlug}`}
        book={book}
        matter={matter}
      />
    );
  }
  return <FreeMatterLoader bookSlug={bookSlug} matterSlug={matterSlug} />;
}

/** The free reading path — unchanged: static corpus → MatterReader. */
function FreeMatterLoader({
  bookSlug,
  matterSlug,
}: {
  bookSlug: string;
  matterSlug: string;
}) {
  const data = useMatterData(bookSlug, matterSlug);
  if (!data) return <ReadingNotFound label="That page could not be found." />;
  return <MatterReader key={`${bookSlug}:${matterSlug}`} data={data} />;
}

/**
 * The entitlement gate for a paywalled matter document. `PremiumChapterGate`'s
 * counterpart, sharing the same `useEntitlementGate` hook so the clock and
 * held-status subtleties exist in one place rather than two.
 *
 * Server-side enforcement is `premium.getMatter` itself — this branching is UX
 * only, and a lapsed session reports `authed: false`, degrading to a sign-in
 * prompt rather than an error.
 */
function PremiumMatterGate({
  book,
  matter,
}: {
  book: CatalogBook;
  matter: CatalogMatter;
}) {
  const unit = lockedMatter(matter);
  // A bespoke VOLUME grant is a chapter range, so the gate is asked about the
  // chapter this document is bound in beside — the same anchor the server
  // resolves in `premium.getMatter`. Without it a comp covering the Odyssey's
  // 13–24 would open every chapter of volume three but not its afterword.
  const config = BOOK_CONFIGS[book.slug];
  const grantChapter =
    config === undefined
      ? undefined
      : anchorChapterOfMatter(config, matter.placement);
  const gate = useEntitlementGate(book, unit, grantChapter);
  if (gate.kind === "loading") return <ReadingSkeleton />;
  if (gate.kind === "entitled") {
    return (
      <EntitledMatterLoader bookSlug={book.slug} matterSlug={matter.slug} />
    );
  }
  return <UpsellPlate book={book} unit={unit} status={gate.status} />;
}

/** Load an entitled paywalled document's data through the gated query. */
function EntitledMatterLoader({
  bookSlug,
  matterSlug,
}: {
  bookSlug: string;
  matterSlug: string;
}) {
  const data = usePremiumMatterData(bookSlug, matterSlug);
  if (data === "loading") return <ReadingSkeleton />;
  if (data === null) {
    return <ReadingNotFound label="That page could not be found." />;
  }
  return <MatterReader key={`${bookSlug}:${matterSlug}`} data={data} />;
}
